/**
 * Login Component
 */

import tr from '../../../../locales';
import { setAuthenticationConfig } from '../../../../redux/actions/authentication';
import * as notificationActions from '../../../../redux/actions/notifications';
import {
  consumePostAuthenticationRequests,
  IPostAuthenticationCreateProjectRequestActionPayload,
  IPostAuthenticationRequest,
  IPostAuthenticationRequestsActionPayload,
  IPostAuthenticationRequestsState,
  recoverPostAuthenticationRequests,
} from '../../../../redux/actions/postAuthenticationRequests';
import { setHeaderType } from '../../../../redux/actions/ui';
import {
  requestSendConfirmationEmail,
  requestUserAuthenticationWithToken,
  setUserProfile,
  setViewerId,
} from '../../../../redux/actions/user';
import { LoginFormFragment } from '../../../../relay/login';
import { decodeFromUrl } from '../../../../utils/url';
import './styles.scss';

import * as classNames from 'classnames';
import { LocationDescriptor, RouteMatch } from 'found';
// import * as imageloaded from 'imagesloaded';
import { isEqual, template } from 'lodash';
import * as nextTick from 'next-tick';
import { parse } from 'query-string';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { connect } from 'react-redux';
import { createFragmentContainer } from 'react-relay';
import * as WindowSizeListener from 'react-window-size-listener';
import * as Velocity from 'velocity-animate';

import Page from '../../../../containers/Page';
import { generateQueryStringFromState } from '../../../../contexts/queryString/helpers';
import { getRouteLocation } from '../../../../utils/router';
import Heading from '../../../atoms/text/Heading';
import AuthenticationDrawer, { AuthenticationDrawerAction } from '../../../organisms/modals/AuthenticationDrawer';
import { BrowsePagePath } from '../../browse/Browse';
import { RegistrationPagePath } from '../Registration';
import { VerifyEmailPagePath } from '../VerifyEmail';

// Page Path
export const LoginPagePath = '/login';
export const SignupPagePath = '/signup';

interface ILoginEmailVerificationPassedState extends IVerifyEmailActionPayload {
  stateType: 'EmailVerification';
}

interface ILoginPostAuthenticationRequestsPassedState extends IPostAuthenticationRequestsActionPayload {
  stateType: 'PostAuthenticationRequests';
}

type LoginPassedStateOptions = ILoginEmailVerificationPassedState | ILoginPostAuthenticationRequestsPassedState;

interface IOwnStateProps {
  postAuthenticationRequests: IPostAuthenticationRequestsState;
}

interface IOwnHiddenDispatchProps {
  requestTokenAuthentication: (payload: IRequestUserTokenAuthenticationActionPayload) => void;
}
interface IOwnDispatchProps {
  consumePostAuthenticationRequests(): void;
  initHeader(): void;
  resetHeader(): void;
  pushNotification(notification: INotificationProps): void;
  recoverPostAuthenticationRequests(requests: IPostAuthenticationRequest[]): void;
  requestSendConfirmationEmail(): Promise<any>;
  setAuthenticationConfig(config: GQL.IAuthenticationInfo): void;
  setUserProfile(profile: GQL.IProfile, viewerId: string): void;
}

interface IOwnMergedProps {
  requestTokenAuthentication: (idToken: string) => Promise<any>;
}

interface IOwnProps extends RouteMatch {
  action?: AuthenticationDrawerAction;
  viewer: GQL.IViewer;
}

export type LoginProps = IOwnProps & IOwnStateProps & IOwnDispatchProps & IOwnMergedProps;

interface IOwnState {
  callbackError?: string;
  callbackErrorDescription?: string;
  callbackIdToken?: string;
  callbackState?: string;
  isCallback?: boolean;
  verificationCode?: string;
}

const classes = new BEM('login-page');

class Login extends React.Component<LoginProps, IOwnState> {
  private illustration: HTMLImageElement;
  private illustrationContainer: HTMLDivElement;

  constructor(props) {
    super(props);

    this.state = {};
  }

  async componentDidMount() {
    this.props.initHeader();

    await this.receiveTokensFromUrlFragment();
    this.triggerActionBasedOnState();

    if (this.props.viewer.profile) {
      this.onLoginSuccess();
    } else if (this.props.viewer.authentication_info) {
      this.props.setAuthenticationConfig(this.props.viewer.authentication_info);
    }
  }

  async componentDidUpdate(prevProps: LoginProps) {
    const { location, viewer } = this.props;

    if (!isEqual(prevProps.location, location)) {
      await this.receiveTokensFromUrlFragment(location);
      this.triggerActionBasedOnState();
    } else if (!prevProps.viewer.profile && viewer.profile) {
      this.props.setUserProfile(viewer.profile, viewer.id);
      nextTick(() => this.onLoginSuccess());
    }
  }

  componentWillUnmount() {
    this.props.resetHeader();
  }

  async onLoginSuccess() {
    const {
      pushNotification,
      router,
      viewer,
    } = this.props;

    const profile = viewer.profile!;

    const isNewUser = profile.is_new_user;
    const userHadLoggedInWithEmail = !!!profile.external_provider;

    const loginSuccessMessage = template(tr.login.successMessage)({ first_name: profile.first_name });
    const message = isNewUser ? tr.signup.successMessage : loginSuccessMessage;

    if (isNewUser && userHadLoggedInWithEmail) {
      try {
        await this.props.requestSendConfirmationEmail();
        pushNotification({
          message: 'A confirmation email was sent to you. Please click the link so we can verify your email.',
        });
      } catch (error) {
        pushNotification({
          message: 'We could not deliver the confirmation email. Please contact an administrator.',
          state: 'error',
        });
      }
    }

    pushNotification({ message, state: 'success' });

    let redirectUrl: LocationDescriptor = BrowsePagePath;

    if (this.state.callbackState) {
      const callbackState = decodeFromUrl<LoginPassedStateOptions>(this.state.callbackState);
      switch (callbackState.stateType) {
        case 'EmailVerification':
          redirectUrl = VerifyEmailPagePath(callbackState.email, callbackState.code);
          break;
        case 'PostAuthenticationRequests':
          this.props.recoverPostAuthenticationRequests(callbackState.postAuthenticationRequests);
          callbackState.postAuthenticationRequests.forEach((req) => {
            if (req.requestType === 'createProject') {
              const { searchState } = req.payload as IPostAuthenticationCreateProjectRequestActionPayload;
              redirectUrl = getRouteLocation(BrowsePagePath, generateQueryStringFromState(searchState));
            }
          });
          nextTick(() => this.props.consumePostAuthenticationRequests());
          return;
      }
    } else if (isNewUser) {
      redirectUrl = RegistrationPagePath;
    }

    setTimeout(() => router.push(redirectUrl), 250);
  }

  onResize = () => {
    // imageloaded(this.illustration, () => {
    //   if (this.illustration) {
    //     nextTick(() => {
    //       this.animateIllustration();
    //     });
    //   }
    // });
  }

  render() {
    const {
      isCallback,
    } = this.state;

    const extraClasses = classNames(
      { 'is-callback': isCallback },
    );

    return (
      <Page id="login" className={extraClasses} noIndex>
        {isCallback ? (
          <Heading is="h2" textColor="bw6">Redirecting you back to Bopper...</Heading>
        ) : (
            <>
              <div ref={(node) => this.illustrationContainer = node!} {...classes('illustration')} />
              <AuthenticationDrawer
                action={this.props.action}
                drawerProps={{
                  floatingHeader: true,
                  showCloseButton: false,
                }}
                useAbsoluteLink
                {...classes('form')}
              />
              <WindowSizeListener onResize={this.onResize} />
            </>
          )}
      </Page>
    );
  }

  // @ts-ignore
  private animateIllustration() {
    const imageScrollAllowed =
      this.illustration.getBoundingClientRect().height - this.illustrationContainer.getBoundingClientRect().height;

    if (imageScrollAllowed < 0) {
      this.illustration.style.width = 'auto';
      this.illustration.style.height = '100%';
    } else {
      this.illustration.style.width = '100%';
      this.illustration.style.height = 'auto';
      Velocity(this.illustration, 'stop');
      Velocity(this.illustration, {
        translateY: [`-${imageScrollAllowed}`, 0],
      }, {
          duration: imageScrollAllowed * 60,
          easing: 'linear',
          loop: true,
        });
    }
  }

  private async triggerActionBasedOnState() {
    const {
      pushNotification,
      requestTokenAuthentication,
      router,
    } = this.props;

    const {
      callbackError,
      callbackErrorDescription,
      callbackIdToken,
    } = this.state;

    const fallBackRedirect = () => setTimeout(() => router.go(-1), 250);

    if (callbackIdToken) {
      try {
        const error = await requestTokenAuthentication(callbackIdToken);
        if (error) {
          console.error('Completed with errors:', error);
          pushNotification({
            autoDismiss: false,
            message: tr.errors.genericMessage,
            state: 'error',
          });
          fallBackRedirect();
        }
      } catch (error) {
        console.error(error);
        pushNotification({
          autoDismiss: false,
          message: tr.errors.login.mutationError,
          state: 'error',
        });
        fallBackRedirect();
      }
    } else if (callbackError) {
      console.error(callbackError, callbackErrorDescription);
      pushNotification({
        autoDismiss: false,
        message: tr.errors.genericMessage,
        state: 'error',
      });
      fallBackRedirect();
    }
  }

  private receiveTokensFromUrlFragment = (location = this.props.location) =>
    new Promise((resolve) => {
      const {
        error,
        error_description,
        id_token,
        state,
      } = parse(location.hash);

      this.setState({
        callbackError: error as string,
        callbackErrorDescription: error_description as string,
        callbackIdToken: id_token as string,
        callbackState: state as string,
        isCallback: !!id_token || !!error,
      }, resolve);
    })
}

const ConnectedLogin =
connect<IOwnStateProps, IOwnDispatchProps & IOwnHiddenDispatchProps, IOwnProps, LoginProps, IStoreState>(
  ({ postAuthenticationRequests }) => ({ postAuthenticationRequests }),
  (dispatch) => ({
    consumePostAuthenticationRequests: () => {
      dispatch(consumePostAuthenticationRequests());
    },
    initHeader: () => {
      dispatch(setHeaderType({ type: 'simple' }));
    },
    resetHeader: () => {
      dispatch(setHeaderType({ type: 'extended' }));
    },
    requestSendConfirmationEmail: () => new Promise((resolve, reject) => {
      dispatch(requestSendConfirmationEmail({ promise: { resolve, reject } }));
    }),
    requestTokenAuthentication: (payload) => {
      dispatch(requestUserAuthenticationWithToken(payload));
    },
    pushNotification(notification) {
      dispatch(notificationActions.pushNotification(notification));
    },
    recoverPostAuthenticationRequests: (requests) => {
      dispatch(recoverPostAuthenticationRequests(requests));
    },
    setAuthenticationConfig: (config) => {
      dispatch(setAuthenticationConfig(config));
    },
    setUserProfile: (profile, viewerId) => {
      dispatch(setViewerId({ viewerId }));
      dispatch(setUserProfile({ profile }));
    },
  }),
  (stateProps, { requestTokenAuthentication, ...dispatchProps }, ownProps) => {
    return {
      ...stateProps,
      ...dispatchProps,
      ...ownProps,
      requestTokenAuthentication: (idToken: string) =>
        new Promise((resolve, reject) => {
          requestTokenAuthentication({
            idToken,
            viewerId: ownProps.viewer.id,
            promise: { resolve, reject },
          });
        }),
    };
  },
)(Login);

const RelayLogin = createFragmentContainer(
  ConnectedLogin,
  LoginFormFragment,
);

export default RelayLogin;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/pages/account/Login/index.tsx